import { t } from '@viral-space/shared';
import { isCapacitorNative, isLikelyMobile } from '../input/mobile';
import { COLORS, FONT, GLOW, css, el } from './ui-shared';

/**
 * Storage key for the dismissed-once flag. Bumping the suffix retires
 * the saved state so a repeat prompt reaches every device after a
 * material change to the copy or steps.
 */
const STORAGE_KEY = 'vs_install_prompt_dismissed_v1';

export type InstallPlatform = 'ios' | 'android' | 'other';

/**
 * UA-based platform detection. Kept narrow on purpose: we only need to
 * decide which set of "Add to Home Screen" steps to render. Anything
 * that isn't clearly an iPhone/iPad or an Android handset falls into
 * `other` and gets the generic guidance.
 *
 * iPadOS 13+ reports a desktop UA (`MacIntel` + touch points), so we
 * treat any Mac-shaped UA that also reports >1 touch points as iOS to
 * cover that case — otherwise iPad users would never see the prompt.
 */
export function detectInstallPlatform(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
  maxTouchPoints: number = typeof navigator !== 'undefined'
    ? (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints ?? 0
    : 0,
): InstallPlatform {
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  // iPadOS spoofs macOS — disambiguate via touch.
  if (/Macintosh/i.test(ua) && maxTouchPoints > 1) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Best-effort check for "page is running as an installed PWA / Add-to-
 * Home-Screen app". Two paths:
 *
 *   - `display-mode: standalone` matches on Chromium and modern Safari
 *     when launched from the home-screen icon.
 *   - The legacy iOS-only `navigator.standalone` boolean still works on
 *     older Safari builds where the media query is missing.
 *
 * Either signal returning true means "already installed, no need to
 * nag". Defaults to `false` on non-browser environments (SSR / tests
 * without window) so the prompt logic stays safe to import there.
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    /* matchMedia may throw in some odd embeds */
  }
  const navStandalone = (window.navigator as unknown as { standalone?: boolean })
    ?.standalone;
  return navStandalone === true;
}

/**
 * Pure decision function — exported so it can be unit-tested without
 * bringing the DOM into play.
 */
export function shouldShowInstallPrompt(opts: {
  isMobile: boolean;
  isCapacitor: boolean;
  isStandalone: boolean;
  alreadyDismissed: boolean;
  platform: InstallPlatform;
}): boolean {
  if (!opts.isMobile) return false;
  if (opts.isCapacitor) return false;
  if (opts.isStandalone) return false;
  if (opts.alreadyDismissed) return false;
  // The instructions are only useful for the two platforms we wrote
  // them for — desktop-mode-on-mobile or unknown UA gets nothing.
  if (opts.platform !== 'ios' && opts.platform !== 'android') return false;
  return true;
}

function loadDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* storage blocked (private mode / quota) — modal will reappear next launch */
  }
}

/**
 * Captured `beforeinstallprompt` event on Android Chrome. The browser
 * fires it once per page; we hold the reference so the modal's
 * "Install" button can re-trigger the native install dialog instead of
 * making the user dig through the menu.
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};
let pendingInstallEvent: BeforeInstallPromptEvent | null = null;
let listenerArmed = false;

export function armBeforeInstallPromptCapture(): void {
  if (listenerArmed) return;
  if (typeof window === 'undefined') return;
  listenerArmed = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    pendingInstallEvent = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    pendingInstallEvent = null;
    // The user just accepted an install — no need to keep nagging on
    // future opens that happen to land back in the browser tab before
    // the OS swaps focus to the freshly-installed PWA.
    saveDismissed();
  });
}

/**
 * Show the modal once if appropriate. Returns the modal instance so the
 * caller can dismiss it programmatically, or `null` if the prompt was
 * skipped.
 */
export function maybeShowInstallPrompt(
  container: HTMLElement,
): InstallPrompt | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const platform = detectInstallPlatform();
  const decision = shouldShowInstallPrompt({
    isMobile: isLikelyMobile(),
    isCapacitor: isCapacitorNative(),
    isStandalone: isStandaloneDisplay(),
    alreadyDismissed: loadDismissed(),
    platform,
  });
  if (!decision) return null;
  return new InstallPrompt(container, platform);
}

/**
 * Visual modal explaining how to install Zerion to the device home
 * screen. Self-contained: builds and tears down its own DOM, persists
 * dismissal in localStorage, and uses the same green-on-black aesthetic
 * as the rest of the auth/menu surfaces.
 */
export class InstallPrompt {
  private readonly root: HTMLElement;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private disposed = false;

  constructor(container: HTMLElement, platform: InstallPlatform) {
    this.root = el('div', {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0,0,0,0.78)',
      backdropFilter: 'blur(2px)',
      display: 'flex',
      alignItems: 'safe center',
      justifyContent: 'safe center',
      zIndex: '20000',
      fontFamily: FONT,
      color: COLORS.primary,
      padding: 'max(16px, env(safe-area-inset-top, 0px)) max(16px, env(safe-area-inset-right, 0px)) max(16px, env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px))',
      overflowY: 'auto',
      // Body sets touch-action: none for the game canvas; restore
      // vertical pan inside the modal so long copy stays scrollable on
      // small landscape phones.
      touchAction: 'pan-y',
      animation: 'vs-fade-in 0.25s ease-out',
    });
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', t('install.title'));

    const card = this.buildCard(platform);
    this.root.appendChild(card);
    container.appendChild(this.root);

    // Tap on the dimmed backdrop dismisses, mirroring the rest of the
    // game's modal conventions. The card itself swallows clicks so
    // taps inside the card don't bubble up and close it.
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.dismiss();
    });
    card.addEventListener('click', (e) => e.stopPropagation());

    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.dismiss();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);
  }

  private buildCard(platform: InstallPlatform): HTMLElement {
    const card = el('div', {
      position: 'relative',
      maxWidth: '440px',
      width: 'min(92vw, 440px)',
      background: 'rgba(0,12,0,0.96)',
      border: `1px solid ${COLORS.primary}`,
      boxShadow: `0 0 24px rgba(0,255,65,0.25), inset 0 0 16px rgba(0,255,65,0.05)`,
      padding: 'clamp(20px, 4vw, 32px)',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      letterSpacing: '1px',
    });

    const closeBtn = el('button', {
      position: 'absolute',
      top: '6px',
      right: '8px',
      width: '32px',
      height: '32px',
      padding: '0',
      fontFamily: FONT,
      fontSize: '18px',
      lineHeight: '1',
      background: 'transparent',
      border: 'none',
      color: COLORS.primary,
      cursor: 'pointer',
      textShadow: GLOW,
      pointerEvents: 'auto',
    }, '\u2715');
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', () => this.dismiss());
    card.appendChild(closeBtn);

    const title = el('div', {
      fontSize: 'clamp(16px, 4vw, 20px)',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      textShadow: GLOW,
      marginRight: '24px',
    }, t('install.title'));
    card.appendChild(title);

    const body = el('div', {
      fontSize: 'clamp(11px, 2.6vw, 13px)',
      lineHeight: '1.55',
      color: 'rgba(0,255,65,0.85)',
      textTransform: 'none',
      letterSpacing: '0.4px',
    }, t('install.body'));
    card.appendChild(body);

    const divider = el('div', {
      height: '1px',
      width: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.4), transparent)',
      margin: '4px 0',
    });
    card.appendChild(divider);

    if (platform === 'ios') {
      this.appendIosSteps(card);
    } else {
      this.appendAndroidSteps(card);
    }

    const dismiss = el('button', {
      marginTop: '8px',
      fontFamily: FONT,
      fontSize: '11px',
      padding: '10px 16px',
      background: 'transparent',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      pointerEvents: 'auto',
    }, t('install.dismiss')) as HTMLButtonElement;
    dismiss.addEventListener('click', () => this.dismiss());
    card.appendChild(dismiss);

    return card;
  }

  private appendIosSteps(parent: HTMLElement): void {
    const heading = el('div', {
      fontSize: 'clamp(11px, 2.6vw, 13px)',
      letterSpacing: '2px',
      color: COLORS.secondary,
      textShadow: '0 0 6px rgba(255,176,0,0.4)',
    }, t('install.iosTitle'));
    parent.appendChild(heading);

    const list = this.buildStepList([
      this.iosStep1WithIcon(),
      t('install.iosStep2'),
      t('install.iosStep3'),
    ]);
    parent.appendChild(list);

    const note = el('div', {
      fontSize: '10px',
      color: COLORS.dimText,
      textTransform: 'none',
      letterSpacing: '0.3px',
      lineHeight: '1.5',
      fontStyle: 'italic',
    }, t('install.iosNote'));
    parent.appendChild(note);
  }

  /**
   * iOS step 1 reads "Tap the Share button …". The original sentence
   * already names the control, so we just prepend a small inline SVG
   * of the standard iOS share glyph as a visual anchor — a player who
   * isn't sure which button "Share" is can match the icon against the
   * Safari toolbar instead of having to translate the word.
   */
  private iosStep1WithIcon(): HTMLElement {
    const wrap = el('span', {
      display: 'inline',
      whiteSpace: 'normal',
    });
    const iconWrap = el('span', {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      verticalAlign: 'middle',
      marginRight: '4px',
    });
    iconWrap.innerHTML = `
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"
           style="vertical-align: middle; filter: drop-shadow(0 0 3px rgba(0,255,65,0.5));">
        <path d="M12 3 L8 7 M12 3 L16 7 M12 3 V14"
              stroke="#00ff41" stroke-width="1.6" fill="none" stroke-linecap="round"/>
        <path d="M6 11 H5 V20 H19 V11 H18"
              stroke="#00ff41" stroke-width="1.6" fill="none" stroke-linejoin="round"/>
      </svg>`;
    wrap.appendChild(iconWrap);
    const text = document.createElement('span');
    text.textContent = t('install.iosStep1');
    wrap.appendChild(text);
    return wrap;
  }

  private appendAndroidSteps(parent: HTMLElement): void {
    const heading = el('div', {
      fontSize: 'clamp(11px, 2.6vw, 13px)',
      letterSpacing: '2px',
      color: COLORS.secondary,
      textShadow: '0 0 6px rgba(255,176,0,0.4)',
    }, t('install.androidTitle'));
    parent.appendChild(heading);

    // If the browser already fired beforeinstallprompt, expose a one-
    // tap install button. We still render the manual steps below it
    // because not every Chromium build qualifies for the native
    // dialog (PWA criteria are stricter on some forks).
    if (pendingInstallEvent) {
      const install = el('button', {
        fontFamily: FONT,
        fontSize: 'clamp(12px, 2.8vw, 14px)',
        padding: '10px 20px',
        background: 'transparent',
        border: `2px solid ${COLORS.primary}`,
        color: COLORS.primary,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '3px',
        boxShadow: '0 0 8px rgba(0,255,65,0.3)',
        animation: 'vs-pulse 2s ease-in-out infinite',
        alignSelf: 'flex-start',
        pointerEvents: 'auto',
      }, t('install.androidInstallBtn')) as HTMLButtonElement;
      install.addEventListener('click', async () => {
        const evt = pendingInstallEvent;
        if (!evt) return;
        try {
          await evt.prompt();
          const choice = await evt.userChoice;
          // Either outcome retires the event for this page; the spec
          // forbids re-prompting with the same instance.
          pendingInstallEvent = null;
          if (choice.outcome === 'accepted') this.dismiss();
        } catch {
          // The browser refused to show the prompt (e.g. the gesture
          // anchor expired). Leave the modal open so the user can
          // still follow the manual steps below.
        }
      });
      parent.appendChild(install);
    }

    const list = this.buildStepList([
      t('install.androidStep1'),
      t('install.androidStep2'),
      t('install.androidStep3'),
    ]);
    parent.appendChild(list);
  }

  private buildStepList(steps: Array<string | HTMLElement>): HTMLElement {
    const list = el('ol', {
      margin: '0',
      padding: '0 0 0 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontSize: 'clamp(11px, 2.6vw, 13px)',
      lineHeight: '1.5',
      color: 'rgba(0,255,65,0.85)',
      textTransform: 'none',
      letterSpacing: '0.4px',
    });
    for (const step of steps) {
      const li = document.createElement('li');
      css(li, { paddingLeft: '4px' });
      if (typeof step === 'string') {
        li.textContent = step;
      } else {
        li.appendChild(step);
      }
      list.appendChild(li);
    }
    return list;
  }

  /** User explicitly closed the modal — persist the preference. */
  private dismiss(): void {
    saveDismissed();
    this.dispose();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }
}
